"use client";

import { Crown, Medal } from "lucide-react";
import { useTranslations } from "next-intl";

import type { ArenaGame, ArenaStanding } from "@/shared/api/arena";
import { cn } from "@/shared/lib/utils";

interface ArenaStandingsTableProps {
  standings: ArenaStanding[];
  games: ArenaGame[];
}

/** Лидерборд Arena: место, команда, очки, разбивка мест по играм. */
export function ArenaStandingsTable({ standings, games }: ArenaStandingsTableProps) {
  const tUi = useTranslations("Ui");
  if (standings.length === 0) {
    return (
      <div className="rounded-xl border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground">
        {tUi("arenaStandingsTable.komandyPoyavyatsyaPosleZapuska")}
      </div>
    );
  }

  const completedGames = games
    .filter((g) => g.status === "Completed")
    .sort((a, b) => a.gameNumber - b.gameNumber);

  return (
    <div className="overflow-x-auto rounded-2xl border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/40 text-xs text-muted-foreground">
            <th className="px-3 py-2 text-left font-medium">#</th>
            <th className="px-3 py-2 text-left font-medium">{tUi("arenaStandingsTable.komanda")}</th>
            <th className="px-3 py-2 text-right font-medium">{tUi("arenaStandingsTable.ochki")}</th>
            {completedGames.map((g) => (
              <th key={g.id} className="px-2 py-2 text-center font-medium">
                И{g.gameNumber}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {standings.map((s) => (
            <tr key={s.tournamentTeamId} className="border-b last:border-0 hover:bg-muted/30">
              <td className="px-3 py-2">
                <span className="inline-flex items-center gap-1 font-semibold">
                  {s.rank === 1 ? (
                    <Crown className="h-4 w-4 text-yellow-500" />
                  ) : s.rank <= 3 ? (
                    <Medal
                      className={cn(
                        "h-4 w-4",
                        s.rank === 2 ? "text-zinc-400" : "text-amber-700",
                      )}
                    />
                  ) : null}
                  {s.rank}
                </span>
              </td>
              <td className="px-3 py-2">
                <span className="font-medium">{s.teamName}</span>
                {s.teamTag && (
                  <span className="ml-1.5 text-xs text-muted-foreground">[{s.teamTag}]</span>
                )}
                {s.firstPlaces > 0 && (
                  <span className="ml-2 text-[11px] text-yellow-500">
                    🥇×{s.firstPlaces}
                  </span>
                )}
              </td>
              <td className="px-3 py-2 text-right font-mono font-bold">{s.totalPoints}</td>
              {completedGames.map((g, idx) => {
                const place = s.placementsByGame[idx] ?? 0;
                return (
                  <td
                    key={g.id}
                    className={cn(
                      "px-2 py-2 text-center font-mono text-xs",
                      place === 1 && "font-bold text-yellow-500",
                      place === 0 && "text-muted-foreground/40",
                    )}
                  >
                    {place === 0 ? "—" : place}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
